import {
  ORGANIZATION_KINDS,
  PLAN_KEYS,
  organizationProvisionSchema,
  type OrganizationProvision,
} from '@sentinel/shared';
import { Button, InputField, Modal, SelectField } from '@sentinel/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { slugOf } from './slug';

const K = 'console.organizations.form';
type Field = 'name' | 'slug' | 'emailDomains' | 'email' | 'displayName';

interface Props {
  busy: boolean;
  onClose: () => void;
  onSubmit: (input: OrganizationProvision) => void;
}

const emptyForm = () => ({
  name: '',
  slug: '',
  kind: 'civil_protection' as OrganizationProvision['kind'],
  planKey: 'operational' as OrganizationProvision['planKey'],
  domains: '',
  email: '',
  displayName: '',
});

/** A new organization in one step: identity, package, e-mail domains and its first Super Admin,
 *  who is invited by e-mail. Validated with the server's schema. */
export function NewOrganizationModal({ busy, onClose, onSubmit }: Props) {
  const { t } = useTranslation();
  const [form, setForm] = useState(emptyForm);
  const [slugTouched, setSlugTouched] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const set = (patch: Partial<typeof form>) => setForm({ ...form, ...patch });

  const submit = () => {
    const parsed = organizationProvisionSchema.safeParse({
      name: form.name,
      slug: form.slug,
      kind: form.kind,
      planKey: form.planKey,
      emailDomains: form.domains
        .split(',')
        .map((d) => d.trim())
        .filter(Boolean),
      superAdmin: { email: form.email, displayName: form.displayName },
    });
    if (parsed.success) return onSubmit(parsed.data);
    const next: Partial<Record<Field, string>> = {};
    for (const issue of parsed.error.issues) {
      const field = (issue.path[0] === 'superAdmin' ? issue.path[1] : issue.path[0]) as Field;
      next[field] = t(`${K}.errors.${field}`);
    }
    setErrors(next);
  };

  return (
    <Modal
      open
      wide
      title={t(`${K}.title`)}
      icon="building"
      onClose={onClose}
      closeLabel={t('common.actions.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
          <Button variant="primary" disabled={busy} onClick={submit}>
            {t(`${K}.submit`)}
          </Button>
        </>
      }
    >
      <InputField
        id="org-name"
        label={t(`${K}.name`)}
        value={form.name}
        error={errors.name}
        autoFocus
        onChange={(e) =>
          set({ name: e.target.value, ...(slugTouched ? {} : { slug: slugOf(e.target.value) }) })
        }
      />
      <InputField
        id="org-slug"
        label={t(`${K}.slug`)}
        hint={t(`${K}.slugHint`)}
        value={form.slug}
        error={errors.slug}
        onChange={(e) => {
          setSlugTouched(true);
          set({ slug: e.target.value });
        }}
      />
      <SelectField
        id="org-kind"
        label={t(`${K}.kind`)}
        value={form.kind}
        onChange={(e) => set({ kind: e.target.value as typeof form.kind })}
        options={ORGANIZATION_KINDS.map((k) => ({ value: k, label: t(`console.kinds.${k}`) }))}
      />
      <SelectField
        id="org-plan"
        label={t(`${K}.plan`)}
        value={form.planKey}
        onChange={(e) => set({ planKey: e.target.value as typeof form.planKey })}
        options={PLAN_KEYS.map((p) => ({ value: p, label: t(`console.plans.${p}`) }))}
      />
      <InputField
        id="org-domains"
        label={t(`${K}.domains`)}
        hint={t(`${K}.domainsHint`)}
        value={form.domains}
        error={errors.emailDomains}
        onChange={(e) => set({ domains: e.target.value })}
      />
      <InputField
        id="org-admin-email"
        type="email"
        label={t(`${K}.adminEmail`)}
        value={form.email}
        error={errors.email}
        onChange={(e) => set({ email: e.target.value })}
      />
      <InputField
        id="org-admin-name"
        label={t(`${K}.adminName`)}
        value={form.displayName}
        error={errors.displayName}
        onChange={(e) => set({ displayName: e.target.value })}
      />
    </Modal>
  );
}
